import { useMemo } from "react";
import { calculateNextReviewDueDate } from "./useNextReviewDate";

const useSortSystemsByDueDate = (systems) => {
  return useMemo(() => {
    if (!systems || systems.length === 0) return systems;

    // Group items by their groupKey
    const grouped = new Map();
    systems.forEach(item => {
      const key = item.groupKey;
      if (!grouped.has(key)) {
        grouped.set(key, { header: null, children: [] });
      }
      if (item.isGroupHeader) {
        grouped.get(key).header = item;
      } else {
        grouped.get(key).children.push(item);
      }
    });

    // Sort groups by header's earliest due date
    const sortedGroups = Array.from(grouped.values()).sort((groupA, groupB) => {
      let dateA = groupA.header?.earliestDueDate;
      let dateB = groupB.header?.earliestDueDate;

      // If no header, use first child's date
      if (!dateA && groupA.children.length > 0) {
        dateA = calculateNextReviewDueDate(
          groupA.children[0].last_reviewed_at,
          groupA.children[0].review_cycle,
          groupA.children[0].created_at
        );
      }
      if (!dateB && groupB.children.length > 0) {
        dateB = calculateNextReviewDueDate(
          groupB.children[0].last_reviewed_at,
          groupB.children[0].review_cycle,
          groupB.children[0].created_at
        );
      }

      // Handle null/undefined dates - put them at the end
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      // Convert to timestamps for comparison
      const timestampA = dateA instanceof Date ? dateA.getTime() : new Date(dateA).getTime();
      const timestampB = dateB instanceof Date ? dateB.getTime() : new Date(dateB).getTime();

      return timestampA - timestampB;
    });

    // Flatten back: header first, then its children
    const sorted = [];
    sortedGroups.forEach(group => {
      if (group.header) {
        sorted.push(group.header);
      }
      // Sort children by their individual due dates
      group.children.sort((a, b) => {
        const dateA = calculateNextReviewDueDate(a.last_reviewed_at, a.review_cycle, a.created_at);
        const dateB = calculateNextReviewDueDate(b.last_reviewed_at, b.review_cycle, b.created_at);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        const timestampA = dateA instanceof Date ? dateA.getTime() : new Date(dateA).getTime();
        const timestampB = dateB instanceof Date ? dateB.getTime() : new Date(dateB).getTime();

        return timestampA - timestampB;
      });
      sorted.push(...group.children);
    });

    return sorted;
  }, [systems]);
};

export default useSortSystemsByDueDate;

